import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
} from "@mui/material";
import { Schedule, Phone, AttachMoney, Inventory } from "@mui/icons-material";

export default function ProblemSection() {
  const problems = [
    {
      icon: <Schedule sx={{ fontSize: 40, color: "#ef4444" }} />,
      title: "Drowning in Admin Tasks",
      description: "Spending 8+ hours weekly on scheduling, billing, and inventory management instead of patient care"
    },
    {
      icon: <Phone sx={{ fontSize: 40, color: "#ef4444" }} />,
      title: "Missing Patient Calls",
      description: "Losing revenue from missed appointments due to outdated scheduling systems"
    },
    {
      icon: <AttachMoney sx={{ fontSize: 40, color: "#ef4444" }} />,
      title: "Billing Nightmares",
      description: "Struggling with billing errors that cost time and damage patient relationships"
    },
    {
      icon: <Inventory sx={{ fontSize: 40, color: "#ef4444" }} />,
      title: "Inventory Chaos",
      description: "Wrestling with inventory tracking that leads to stockouts and overordering"
    }
  ];

  return (
    <Box sx={{ background: "linear-gradient(to bottom, #f9fafb, #ffffff)", py: 10 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              mb: 3, 
              color: "#1f2937",
              fontSize: { xs: "2.5rem", lg: "3rem" },
              fontWeight: 700,
            }}
          >
            Does This Sound Familiar?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6b7280",
              maxWidth: 800,
              mx: "auto",
              fontSize: "20px",
              lineHeight: 1.6,
            }}
          >
            Each week, you're battling outdated software that costs you precious hours. 
            You're losing patients to scheduling conflicts and missed calls, struggling 
            with billing errors, and drowning in inventory nightmares.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {problems.map((problem, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Card
                sx={{
                  height: "100%",
                  textAlign: "center",
                  p: 4,
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box 
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      backgroundColor: "#fef2f2", 
                      borderRadius: "50%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      mx: "auto", 
                      mb: 3 
                    }}
                  >
                    {problem.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1f2937", fontSize: "20px" }}
                  >
                    {problem.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", lineHeight: 1.6, fontSize: "16px" }}
                  >
                    {problem.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pain Point Testimonial */}
        <Paper
          sx={{
            bgcolor: "#fef2f2",
            p: { xs: 4, lg: 6 },
            borderLeft: "8px solid #ef4444",
            borderRadius: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
            <Avatar
              src="https://readdy.ai/api/search-image?query=Professional%20stressed%20optometrist%20doctor%20in%20white%20coat%20looking%20overwhelmed%20with%20paperwork%20in%20modern%20clinic%20office%2C%20tired%20expression%2C%20surrounded%20by%20administrative%20tasks%2C%20realistic%20professional%20headshot%20photography%20with%20warm%20lighting&width=120&height=120&seq=stressed-doc-001&orientation=squarish"
              sx={{ width: 80, height: 80, flexShrink: 0 }}
            />
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  mb: 2,
                  fontSize: { xs: "18px", lg: "20px" },
                  lineHeight: 1.6,
                  color: "#374151",
                }}
              >
                "I was spending more time on administrative tasks than actually
                examining patients. The constant interruptions for scheduling
                conflicts and billing issues were driving me crazy. I knew there
                had to be a better way..."
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#ef4444", fontWeight: 600, fontSize: "16px" }}
              >
                - Dr. Sarah Martinez, Before Using Our Software
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
